import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setDTEConfiguration,
  updateDTEConfigurationField,
  resetDTEConfiguration,
  setLoading,
  setSubmitting,
  setError,
  setEnvironments,
  setCurrencies,
  setBillingModels,
  setTransmissionTypes,
} from '../../store/slices/hacienda/dteConfigurationSlice';

import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';
import DTEConfigurationService from '../../services/hacienda/DTEConfigurationService';
import { DTEConfigurationForm } from '../../types/hacienda/DTEConfigurationTypes';

export const useDTEConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    form,
    environments,
    currencies,
    billingModels,
    transmissionTypes,
    isLoading,
    isSubmitting,
    error,
  } = useSelector((state: RootState) => state.dteConfiguration);

  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  /**
   * ✅ Cargar configuración actual
   */
  const loadConfiguration = async () => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      const response = await DTEConfigurationService.getConfiguration();
      if (response.statusCode === 200) {
        dispatch(setDTEConfiguration(response.data));
      } else {
        dispatch(setError('Could not load DTE configuration.'));
        showAlert('error', 'Error', 'Could not get DTE configuration.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
 * ✅ Guardar o actualizar configuración
 */
  const saveConfiguration = async (form: DTEConfigurationForm) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const payload = {
        id: form.id,
        environmentId: form.environmentId,
        currencyId: form.currencyId,
        billingModelId: form.billingModelId,
        transmissionTypeId: form.transmissionTypeId,
        versionInvalidation: form.versionInvalidation,
        active: form.active,
      };

      let response;
      if (form.id) {
        response = await DTEConfigurationService.updateConfiguration(form.id, payload);
      } else {
        response = await DTEConfigurationService.createConfiguration(payload);
      }

      if (response.statusCode === 200 || response.statusCode === 201) {
        dispatch(setDTEConfiguration(response.data));
        showAlert(response.type, response.title, response.message);
      } else {
        showAlert('error', 'Error', 'Could not save DTE configuration.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };


  /**
   * ✅ Cargar listas de dropdowns
   */
  const loadDropdowns = async () => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      // Environments
      const envResp = await DTEConfigurationService.getEnvironments();
      if (envResp.statusCode === 200) {
        dispatch(setEnvironments(envResp.data));
      }

      // Currencies
      const currResp = await DTEConfigurationService.getCurrencies();
      if (currResp.statusCode === 200) {
        dispatch(setCurrencies(currResp.data));
      }

      // Billing Models
      const billingResp = await DTEConfigurationService.getBillingModels();
      if (billingResp.statusCode === 200) {
        dispatch(setBillingModels(billingResp.data));
      }

      // Transmission Types
      const transmissionResp = await DTEConfigurationService.getTransmissionTypes();
      if (transmissionResp.statusCode === 200) {
        dispatch(setTransmissionTypes(transmissionResp.data));
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * ✅ Actualizar un campo individual
   */
  const updateField = <K extends keyof DTEConfigurationForm>(
    field: K,
    value: DTEConfigurationForm[K]
  ) => {
    dispatch(updateDTEConfigurationField({ field, value }));
  };

  /**
   * ✅ Resetear formulario
   */
  const resetForm = () => {
    dispatch(resetDTEConfiguration());
  };

  return {
    form,
    environments,
    currencies,
    billingModels,
    transmissionTypes,
    isLoading,
    isSubmitting,
    error,
    updateField,
    resetForm,
    loadConfiguration,
    saveConfiguration,
    loadDropdowns,
  };
};
